import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { LogoMark } from "@/components/app/Brand";
import { api } from "@/lib/api";
import { ArrowLeft } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    api.get(`/public/blog-posts/${slug}`).then((res) => {
      if (!cancelled) setPost(res.data);
    }).catch(() => {
      if (!cancelled) setNotFound(true);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [slug]);

  // Per-post <title> and meta description, so the page isn't indexed (or
  // shared) under the site-wide homepage title.
  useEffect(() => {
    if (!post) return undefined;
    const prevTitle = document.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content");
    document.title = `${post.title} | CombinedWork Blog`;
    if (meta && post.excerpt) meta.setAttribute("content", post.excerpt);
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc != null) meta.setAttribute("content", prevDesc);
    };
  }, [post]);

  return (
    <div className="bg-white text-gray-900 min-h-screen font-sans">
      <nav className="border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-violet-600 grid place-items-center shadow-md shadow-violet-200">
              <LogoMark size={16} color="#fff" />
            </div>
            <span className="text-base font-bold text-gray-900 tracking-tight">Combined<span className="text-violet-600">Work</span></span>
          </Link>
          <Link to="/blog" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to blog
          </Link>
        </div>
      </nav>

      {loading && (
        <p className="text-center text-sm text-gray-400 py-24" data-testid="blog-post-loading">Loading…</p>
      )}

      {!loading && notFound && (
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <h1 className="font-display font-bold text-2xl text-gray-900 mb-3">Post not found</h1>
          <p className="text-gray-500 text-sm mb-8">This post may have been unpublished or moved.</p>
          <Link to="/blog" className="text-sm font-semibold text-violet-600 hover:text-violet-700">Browse all posts</Link>
        </div>
      )}

      {!loading && !notFound && post && (
        <article className="max-w-2xl mx-auto px-6 pt-16 pb-24">
          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-5">
              {post.tags.map((t) => (
                <span key={t} className="px-3 py-1 bg-violet-100 text-violet-600 text-xs font-semibold rounded-full">{t}</span>
              ))}
            </div>
          )}
          <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tight text-gray-900 mb-4">{post.title}</h1>
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-10">
            {post.author_name && <span>{post.author_name}</span>}
            {post.author_name && post.published_at && <span>&middot;</span>}
            {post.published_at && (
              <span>{new Date(post.published_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</span>
            )}
          </div>
          {post.cover_image && (
            <img src={post.cover_image} alt="" className="w-full rounded-2xl mb-10 object-cover max-h-96" />
          )}
          {/* content_html is rendered + sanitized server-side (services/blog_html.py),
              turning the Markdown/HTML body into real <h2>/<h3> headings. */}
          <div
            className="blog-content text-gray-700 text-base leading-relaxed"
            data-testid="blog-post-content"
            dangerouslySetInnerHTML={{ __html: post.content_html || "" }}
          />
        </article>
      )}

      <footer className="border-t border-gray-100 py-8 px-6 text-center">
        <span className="text-xs text-gray-400">&copy; {new Date().getFullYear()} CombinedWork. All rights reserved.</span>
      </footer>
    </div>
  );
}
